import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../Config.js";
import styles from "./FilterEditStock.module.css";

// ------------------------------------------------------------------
// Column model. `kind`: 'readonly' | 'text' | 'number' (decimal) | 'int' (qty)
// `width` is the default px width (user can resize from the header).
// ------------------------------------------------------------------
const COLUMNS = [
  { key: "sno",            label: "S.No",               kind: "readonly", width: 64 },
  { key: "itemCode",       label: "Item Code",          kind: "text",     width: 110 },
  { key: "description",    label: "Item Description",   kind: "text",     width: 200 },
  { key: "itemType",       label: "Item Type",          kind: "text",     width: 95 },
  { key: "itemColor",      label: "Item Color",         kind: "text",     width: 110 },
  { key: "itemSize",       label: "Item Size",          kind: "text",     width: 80 },
  { key: "itemCategory",   label: "School Name",        kind: "text",     width: 150 },
  { key: "price",          label: "Price",              kind: "number",   width: 78 },
  { key: "wholeSalePrice", label: "Wholesale Price",    kind: "number",   width: 96 },
  { key: "quantity",       label: "Available Qty",      kind: "int",      width: 120 },
  { key: "group_id",       label: "Group ID",           kind: "text",     width: 88 },
];
const EDITABLE_COLS = COLUMNS.filter((c) => c.kind !== "readonly");
const EDITABLE_INDEX = {};
EDITABLE_COLS.forEach((c, i) => { EDITABLE_INDEX[c.key] = i; });
const GROUPABLE = ["itemType", "itemColor", "itemSize", "itemCategory", "group_id"];
const ACTION_KEY = "__action";
const ALL_SCOPE = "__all__";

// Filter-type cards shown in the "Choose data to edit" chooser.
const FILTER_TYPES = [
  { val: "filter1", lbl: "School & Item", desc: "Pick a school, then an item type" },
  { val: "filter2", lbl: "Item",          desc: "Load by item type only" },
  { val: "filter3", lbl: "School",        desc: "All items for one school" },
  { val: "filter4", lbl: "All Items",     desc: "Load the entire inventory" },
];

// Small inline icon for each filter-type card.
const TypeIcon = ({ type }) => {
  const p = {
    width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round",
  };
  switch (type) {
    case "filter1":
      return (<svg {...p}><path d="M3 21h18" /><path d="M5 21V9l7-4 7 4v12" /><path d="M9.5 21v-5h5v5" /></svg>);
    case "filter2":
      return (<svg {...p}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>);
    case "filter3":
      return (<svg {...p}><path d="M3 21h18" /><path d="M6 21V8l6-4 6 4v13" /><path d="M10 13h4M10 17h4" /></svg>);
    case "filter4":
      return (<svg {...p}><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></svg>);
    default:
      return null;
  }
};

const getRowId = (item) => String(item.itemBarcodeID ?? item.sno);
const cx = (...n) => n.filter(Boolean).join(" ");
const norm = (v) => (v == null || v === "" ? "(Blanks)" : String(v));

// ==================================================================
// Memoized row. Re-renders ONLY when its own props change:
//  - rowEdit (reference is stable for untouched rows, so typing in one
//    row never re-renders the others)
//  - fillField (string/value, compared by value)
//  - rowIdx / item (stable while not re-sorting)
// All handlers are stable (useCallback) so they never break memo.
// ==================================================================
const Row = React.memo(function Row({
  item,
  rowEdit,
  rowIdx,
  fillField,
  onFieldChange,
  onNumberChange,
  onCellKeyDown,
  onPaste,
  onStartFill,
  registerRef,
  onRowMouseEnter,
}) {
  const id = getRowId(item);

  return (
    <tr onMouseEnter={() => onRowMouseEnter(rowIdx)}>
      {COLUMNS.map((col) => {
        if (col.kind === "readonly") {
          return <td key={col.key} className={styles.roCell}>{item[col.key]}</td>;
        }

        const has = rowEdit && col.key in rowEdit;
        const val = has ? (rowEdit[col.key] ?? "") : (item[col.key] ?? "");
        const changed = has && String(rowEdit[col.key] ?? "") !== String(item[col.key] ?? "");
        const inFill = fillField === col.key;
        const tdCls = cx(
          styles.cell,
          changed && styles.cellChanged,
          inFill && styles.fillTarget
        );
        const refCb = (el) => registerRef(`${rowIdx}_${col.key}`, el);

        if (col.kind === "int") {
          const current = parseInt(val, 10) || 0;
          return (
            <td key={col.key} className={tdCls}>
              <div className={styles.qtyContainer}>
                <button
                  type="button"
                  className={cx(styles.qtyBtn, styles.qtyBtnDec)}
                  onClick={() => onFieldChange(id, "quantity", Math.max(0, current - 1))}
                >−</button>
                <input
                  ref={refCb}
                  type="text"
                  inputMode="numeric"
                  value={val}
                  onChange={(e) => onNumberChange(id, "quantity", e.target.value, true)}
                  onKeyDown={(e) => onCellKeyDown(e, rowIdx, col.key)}
                  onPaste={(e) => onPaste(e, rowIdx, col.key)}
                  className={styles.qtyInput}
                />
                <button
                  type="button"
                  className={cx(styles.qtyBtn, styles.qtyBtnInc)}
                  onClick={() => onFieldChange(id, "quantity", current + 1)}
                >+</button>
              </div>
              <span className={styles.fillHandle} onMouseDown={(e) => onStartFill(e, col.key, item)} />
            </td>
          );
        }

        const numeric = col.kind === "number";
        return (
          <td key={col.key} className={tdCls}>
            <input
              ref={refCb}
              type="text"
              inputMode={numeric ? "decimal" : "text"}
              value={val}
              onChange={(e) =>
                numeric
                  ? onNumberChange(id, col.key, e.target.value, false)
                  : onFieldChange(id, col.key, e.target.value)
              }
              onKeyDown={(e) => onCellKeyDown(e, rowIdx, col.key)}
              onPaste={(e) => onPaste(e, rowIdx, col.key)}
              className={cx(styles.input, numeric && styles.inputNum)}
            />
            <span className={styles.fillHandle} onMouseDown={(e) => onStartFill(e, col.key, item)} />
          </td>
        );
      })}
      <td className={styles.actionCell}>
        <button className={styles.deleteBtn}>Delete</button>
      </td>
    </tr>
  );
});

const FilterEditStock = () => {
  // ---------------- filter-selection state ----------------
  const [selectedFilter, setSelectedFilter] = useState("filter1");
  const [schoolList, setSchoolList] = useState([]);
  const [itemTypeList, setItemTypeList] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedItemType, setSelectedItemType] = useState("");
  const [filteredItemTypeList, setFilteredItemTypeList] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(true);
  // Presentation mode for the chooser, decided ONCE when it opens so that
  // live `data` changes can't flip it between inline <-> overlay mid-use.
  // false = inline panel (no dark backdrop), true = overlay modal.
  const [chooserOverlay, setChooserOverlay] = useState(false);

  // ---------------- table / edit state ----------------
  const [editableData, setEditableData] = useState({});
  const [search, setSearch] = useState("");
  const [maxQuantity, setMaxQuantity] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [columnFilters, setColumnFilters] = useState({}); // global: { colKey: [allowedNormValues] }
  const [groupBy, setGroupBy] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  // column widths (resizable)
  const [colWidths, setColWidths] = useState(() => {
    const w = {};
    COLUMNS.forEach((c) => { w[c.key] = c.width; });
    w[ACTION_KEY] = 84;
    return w;
  });

  // popover / modal
  const [activeFilter, setActiveFilter] = useState(null); // { key, x, y }
  const [popSearch, setPopSearch] = useState("");
  const [showDiff, setShowDiff] = useState(false);
  const popoverRef = useRef(null);

  // drag-fill
  const [fill, setFill] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const cellRefs = useRef({});
  const resizing = useRef(null);

  // ----- live refs so handlers can stay stable (no re-render on edits) -----
  const editableDataRef = useRef(editableData);
  const displayOrderRef = useRef([]);
  const indexByIdRef = useRef({});
  const fillRef = useRef(fill);
  const hoverIndexRef = useRef(hoverIndex);
  editableDataRef.current = editableData;
  fillRef.current = fill;
  hoverIndexRef.current = hoverIndex;

  // Cell display reads edited value; pipeline (below) reads ORIGINAL values.
  const getEditedValue = useCallback((item, key) => {
    const ed = editableDataRef.current[getRowId(item)];
    if (ed && key in ed) return ed[key];
    return item[key];
  }, []);

  // ================= data fetching =================
  useEffect(() => {
    const storeId = JSON.parse(sessionStorage.getItem("user") || "{}")?.storeId;
    if (!storeId) return console.error("Store ID not found in session storage");
    axios.get(`${API_BASE_URL}/user/filter/getSchool`, { params: { storeId } })
      .then((r) => setSchoolList(r.data))
      .catch((e) => console.error("Error fetching schools:", e));
  }, []);

  useEffect(() => {
    if (selectedFilter !== "filter1" || !selectedSchool) return;
    const storeId = JSON.parse(sessionStorage.getItem("user") || "{}")?.storeId;
    if (!storeId) return;
    axios.get(`${API_BASE_URL}/user/filter/school/item_type`, {
      params: { schoolCode: selectedSchool, storeId },
    }).then((r) => setFilteredItemTypeList(r.data))
      .catch((e) => console.error("Error fetching item types for school:", e));
  }, [selectedFilter, selectedSchool]);

  useEffect(() => {
    if (selectedFilter !== "filter2") return;
    const storeId = JSON.parse(sessionStorage.getItem("user") || "{}")?.storeId;
    if (!storeId) return;
    axios.get(`${API_BASE_URL}/user/filter/item_type`, { params: { storeId } })
      .then((r) => setItemTypeList(r.data))
      .catch((e) => console.error("Error fetching item types:", e));
  }, [selectedFilter]);

  const fetchData = useCallback(() => {
    const storeId = JSON.parse(sessionStorage.getItem("user") || "{}")?.storeId;
    setLoading(true);
    const done = (r) => { setData(r.data); setLoading(false); };
    const fail = (label) => (e) => { console.error(label, e); setLoading(false); };

    if (selectedFilter === "filter1" && selectedSchool && selectedItemType) {
      axios.get(`${API_BASE_URL}/user/filter/item_category/item_type`, {
        params: { schoolCode: selectedSchool, itemType: selectedItemType, storeId },
      }).then(done).catch(fail("filter1"));
    } else if (selectedFilter === "filter2" && selectedItemType) {
      axios.get(`${API_BASE_URL}/user/filter/item_list_type`, {
        params: { itemType: selectedItemType, storeId },
      }).then(done).catch(fail("filter2"));
    } else if (selectedFilter === "filter3" && selectedSchool) {
      axios.get(`${API_BASE_URL}/user/filter/item_list_school_code`, {
        params: { schoolCode: selectedSchool, storeId },
      }).then(done).catch(fail("filter3"));
    } else if (selectedFilter === "filter4") {
      axios.get(`${API_BASE_URL}/user/filter/getAllItems`, {
        params: { storeId },
      }).then(done).catch(fail("filter4"));
    } else {
      setLoading(false);
    }
  }, [selectedFilter, selectedSchool, selectedItemType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    setEditableData({});
    setColumnFilters({});
    setSortConfig({ key: null, direction: null });
    setGroupBy("");
    setCollapsedGroups({});
    if (data.length > 0) setFilterModalOpen(false);
  }, [data]);

  // ================= filter-selection handlers =================
  // Open as an overlay only when there's already a table behind it; otherwise
  // open inline. Mode is fixed for this open-session (see chooserOverlay).
  const openChooser = () => { setChooserOverlay(data.length > 0); setFilterModalOpen(true); };

  const selectFilterType = (val) => {
    setSelectedFilter(val);
    if (val !== "filter1") setSelectedSchool("");
    setSelectedItemType("");
    setData([]);
  };
  const handleSchoolChange = (e) => { setSelectedSchool(e.target.value); setSelectedItemType(""); };
  const handleItemTypeChange = (e) => setSelectedItemType(e.target.value);

  // ================= edit handlers (stable) =================
  const handleFieldChange = useCallback((id, field, value) =>
    setEditableData((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } })), []);

  const onNumberChange = useCallback((id, key, raw, integer) => {
    const re = integer ? /^[0-9]*$/ : /^-?[0-9]*\.?[0-9]*$/;
    if (raw === "" || re.test(raw)) handleFieldChange(id, key, raw);
  }, [handleFieldChange]);

  const resetEdits = () => {
    if (window.confirm("Discard all unsaved changes and restore original values?"))
      setEditableData({});
  };

  // ================= sort / group =================
  const handleSort = (key) =>
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: null };
    });

  const toggleGroup = (g) => setCollapsedGroups((p) => ({ ...p, [g]: !p[g] }));
  const chooseGroup = (k) => { setGroupBy(k); setCollapsedGroups({}); };

  const clearAll = () => {
    setSearch(""); setMaxQuantity(""); setColumnFilters({});
    setSortConfig({ key: null, direction: null });
    setGroupBy(""); setCollapsedGroups({});
  };

  // ================= column resize =================
  const onResizeMove = useCallback((e) => {
    const r = resizing.current;
    if (!r) return;
    const w = Math.max(44, r.startW + (e.clientX - r.startX));
    setColWidths((prev) => ({ ...prev, [r.key]: w }));
  }, []);
  const onResizeUp = useCallback(() => {
    resizing.current = null;
    document.body.style.cursor = "";
    document.removeEventListener("mousemove", onResizeMove);
    document.removeEventListener("mouseup", onResizeUp);
  }, [onResizeMove]);
  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = { key, startX: e.clientX, startW: colWidths[key] };
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", onResizeMove);
    document.addEventListener("mouseup", onResizeUp);
  };

  // ================= PIPELINE (uses ORIGINAL values, not edits) =================
  // Decoupling from `editableData` means typing never rebuilds the table,
  // and row order stays stable while you edit.
  const baseData = useMemo(() => {
    const s = search.toLowerCase();
    const maxQ = maxQuantity !== "" ? parseInt(maxQuantity, 10) : null;
    return data.filter((item) => {
      if (s) {
        const hit = COLUMNS.some((c) => {
          const v = item[c.key];
          return v != null && String(v).toLowerCase().includes(s);
        });
        if (!hit) return false;
      }
      if (maxQ != null) {
        const q = Number(item.quantity);
        if (!(Number.isFinite(q) && q <= maxQ)) return false;
      }
      return true;
    });
  }, [data, search, maxQuantity]);

  // distinct values for a column, narrowed by every OTHER active column filter
  const distinctFor = useCallback(
    (key, filters) => {
      const base = baseData.filter((it) => {
        for (const k of Object.keys(filters)) {
          if (k === key) continue;
          const allowed = filters[k];
          if (!allowed) continue;
          if (!allowed.includes(norm(it[k]))) return false;
        }
        return true;
      });
      const set = new Set(base.map((it) => norm(it[key])));
      return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    },
    [baseData]
  );

  const openFilter = (e, key) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(r.left, window.innerWidth - 290);
    setPopSearch("");
    setActiveFilter({ key, x: Math.max(8, x), y: r.bottom + 4 });
  };
  const closeFilter = () => { setActiveFilter(null); setPopSearch(""); };

  const isValChecked = (key, v) => {
    const a = columnFilters[key];
    return a ? a.includes(v) : true;
  };
  const toggleValue = (key, v) =>
    setColumnFilters((prev) => {
      const all = distinctFor(key, prev);
      const cur = prev[key] ? [...prev[key]] : [...all];
      const i = cur.indexOf(v);
      if (i >= 0) cur.splice(i, 1); else cur.push(v);
      const next = { ...prev };
      if (cur.length >= all.length) delete next[key];
      else next[key] = cur;
      return next;
    });
  const toggleSelectAll = (key) =>
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!prev[key]) next[key] = []; // uncheck all
      else delete next[key];          // check all
      return next;
    });
  const clearColFilter = (key) =>
    setColumnFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });

  useEffect(() => {
    if (!activeFilter) return;
    const onDown = (ev) => { if (popoverRef.current && !popoverRef.current.contains(ev.target)) closeFilter(); };
    const onKey = (ev) => { if (ev.key === "Escape") closeFilter(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [activeFilter]);

  const filtered = useMemo(() => {
    return baseData.filter((item) => {
      for (const k of Object.keys(columnFilters)) {
        const allowed = columnFilters[k];
        if (!allowed) continue;
        if (allowed.length === 0) return false;
        if (!allowed.includes(norm(item[k]))) return false;
      }
      return true;
    });
  }, [baseData, columnFilters]);

  const applySort = useCallback(
    (items) => {
      const arr = [...items];
      const { key, direction } = sortConfig;
      if (key && direction) {
        const col = COLUMNS.find((c) => c.key === key);
        const numeric = col && (col.kind === "number" || col.kind === "int");
        arr.sort((a, b) => {
          let av = a[key], bv = b[key];
          if (numeric) { av = Number(av) || 0; bv = Number(bv) || 0; return direction === "asc" ? av - bv : bv - av; }
          av = av == null ? "" : String(av); bv = bv == null ? "" : String(bv);
          return direction === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        });
      } else if (maxQuantity !== "") {
        arr.sort((a, b) => (Number(b.quantity) || 0) - (Number(a.quantity) || 0));
      }
      return arr;
    },
    [sortConfig, maxQuantity]
  );

  const groups = useMemo(() => {
    let buckets;
    if (!groupBy) buckets = [{ key: ALL_SCOPE, items: filtered }];
    else {
      const map = new Map();
      filtered.forEach((it) => {
        const g = norm(it[groupBy]);
        if (!map.has(g)) map.set(g, []);
        map.get(g).push(it);
      });
      buckets = Array.from(map.entries()).map(([key, items]) => ({ key, items }));
    }
    return buckets.map((b) => ({ key: b.key, items: applySort(b.items) }));
  }, [filtered, groupBy, applySort]);

  const displayOrder = useMemo(() => {
    const order = [];
    groups.forEach((gp) => { if (!collapsedGroups[gp.key]) order.push(...gp.items); });
    return order;
  }, [groups, collapsedGroups]);

  const indexById = useMemo(() => {
    const m = {};
    displayOrder.forEach((it, i) => { m[getRowId(it)] = i; });
    return m;
  }, [displayOrder]);

  displayOrderRef.current = displayOrder;
  indexByIdRef.current = indexById;

  // ================= change detection / diff =================
  const idToItem = useMemo(() => {
    const m = {};
    data.forEach((it) => { m[getRowId(it)] = it; });
    return m;
  }, [data]);

  const diffs = useMemo(() => {
    const out = [];
    Object.keys(editableData).forEach((id) => {
      const orig = idToItem[id];
      if (!orig) return;
      const changes = [];
      Object.keys(editableData[id]).forEach((k) => {
        const nv = editableData[id][k];
        if (String(nv ?? "") !== String(orig[k] ?? "")) {
          const col = COLUMNS.find((c) => c.key === k);
          changes.push({ key: k, label: col ? col.label : k, old: orig[k], next: nv });
        }
      });
      if (changes.length) out.push({ id, item: orig, changes });
    });
    return out;
  }, [editableData, idToItem]);

  const hasEdits = diffs.length > 0;

  // ================= keyboard navigation (stable, arrows + enter only) =================
  const focusCell = useCallback((rowIdx, colIdx) => {
    const tcol = EDITABLE_COLS[colIdx];
    if (!tcol) return;
    const el = cellRefs.current[`${rowIdx}_${tcol.key}`];
    if (el) { el.focus(); if (el.select) el.select(); }
  }, []);

  const handleCellKeyDown = useCallback((e, rowIdx, colKey) => {
    const colIdx = EDITABLE_INDEX[colKey];
    if (e.key === "Escape") { e.target.blur(); return; }

    const input = e.target;
    const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
    const atEnd =
      input.selectionStart === (input.value?.length ?? 0) &&
      input.selectionEnd === (input.value?.length ?? 0);
    switch (e.key) {
      case "ArrowDown":
      case "Enter": e.preventDefault(); focusCell(rowIdx + 1, colIdx); break;
      case "ArrowUp": e.preventDefault(); focusCell(rowIdx - 1, colIdx); break;
      case "ArrowRight": if (atEnd) { e.preventDefault(); focusCell(rowIdx, colIdx + 1); } break;
      case "ArrowLeft": if (atStart) { e.preventDefault(); focusCell(rowIdx, colIdx - 1); } break;
      default: break;
    }
  }, [focusCell]);

  const sanitizeFor = (col, v) => {
    if (!col) return v;
    if (col.kind === "int") return String(v).replace(/[^0-9]/g, "");
    if (col.kind === "number") return String(v).replace(/[^0-9.\-]/g, "");
    return String(v);
  };

  // ================= paste (Excel block paste, stable) =================
  // A single value pastes normally into the input; a tab/newline block drops
  // starting at the cell you paste into and spreads right/down.
  const handlePaste = useCallback((e, rowIdx, colKey) => {
    const text = e.clipboardData.getData("text");
    if (text == null) return;
    if (!/[\t\n]/.test(text)) return; // single value -> let the input handle it

    e.preventDefault();
    const raw = text.replace(/\r/g, "").split("\n");
    if (raw.length && raw[raw.length - 1] === "") raw.pop();
    const matrix = raw.map((l) => l.split("\t"));
    const order = displayOrderRef.current;
    const startC = EDITABLE_INDEX[colKey];

    setEditableData((prev) => {
      const next = { ...prev };
      for (let i = 0; i < matrix.length; i++) {
        const item = order[rowIdx + i]; if (!item) continue;
        const id = getRowId(item); const rowEdit = { ...next[id] };
        for (let j = 0; j < matrix[i].length; j++) {
          const col = EDITABLE_COLS[startC + j]; if (!col) continue;
          rowEdit[col.key] = sanitizeFor(col, matrix[i][j]);
        }
        next[id] = rowEdit;
      }
      return next;
    });
  }, []);

  // ================= drag-to-fill (stable) =================
  const startFill = useCallback((e, field, item) => {
    e.preventDefault(); e.stopPropagation();
    const id = getRowId(item);
    const src = indexByIdRef.current[id];
    setFill({ field, value: getEditedValue(item, field), sourceIndex: src });
    setHoverIndex(src);
  }, [getEditedValue]);

  const onRowMouseEnter = useCallback((idx) => {
    if (fillRef.current) setHoverIndex(idx);
  }, []);

  useEffect(() => {
    if (!fill) return;
    const onUp = () => {
      const hi = hoverIndexRef.current;
      const order = displayOrderRef.current;
      if (hi != null) {
        const lo = Math.min(fill.sourceIndex, hi);
        const hh = Math.max(fill.sourceIndex, hi);
        setEditableData((prev) => {
          const next = { ...prev };
          for (let i = lo; i <= hh; i++) {
            const it = order[i];
            if (!it) continue;
            next[getRowId(it)] = { ...next[getRowId(it)], [fill.field]: fill.value };
          }
          return next;
        });
      }
      setFill(null); setHoverIndex(null);
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [fill]);

  useEffect(() => {
    document.body.style.userSelect = fill ? "none" : "";
    return () => { document.body.style.userSelect = ""; };
  }, [fill]);

  const registerRef = useCallback((key, el) => { cellRefs.current[key] = el; }, []);

  // ================= submit =================
  const handleSubmitClick = () => {
    if (!hasEdits) { window.alert("No changes to submit."); return; }
    setShowDiff(true);
  };
  const confirmSubmit = async () => {
    setShowDiff(false);
    setIsUpdating(true);
    setStatusMessage("");
    const rowsToUpdate = diffs.map((d) => ({ ...d.item, ...editableData[d.id] }));
    try {
      const response = await axios.post(`${API_BASE_URL}/inventory/edit`, rowsToUpdate, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = "inventory_update.txt"; link.click();
      URL.revokeObjectURL(url);
      setStatusMessage(new TextDecoder("utf-8").decode(response.data));
      setEditableData({});
      fetchData();
    } catch (err) {
      console.error("Error updating inventory:", err);
      setStatusMessage("Failed to update items. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ================= render helpers =================
  const filterSummary = () => {
    const labels = { filter1: "School + Item", filter2: "Item", filter3: "School", filter4: "All Items" };
    let s = labels[selectedFilter] || "";
    if (selectedSchool) s += ` · ${selectedSchool}`;
    if (selectedItemType) s += ` · ${selectedItemType}`;
    return s;
  };

  const popValues = activeFilter
    ? distinctFor(activeFilter.key, columnFilters).filter((v) => v.toLowerCase().includes(popSearch.toLowerCase()))
    : [];
  const popAllChecked = activeFilter ? !columnFilters[activeFilter.key] : false;

  const SearchIcon = () => (
    <svg className={styles.searchIcon} viewBox="0 0 24 24" width="16" height="16" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );

  // fill range (computed once per render; cheap)
  const fillLo = fill && hoverIndex != null ? Math.min(fill.sourceIndex, hoverIndex) : -1;
  const fillHi = fill && hoverIndex != null ? Math.max(fill.sourceIndex, hoverIndex) : -2;

  const hasData = data.length > 0;

  // ----- the chooser content (shared by inline card AND overlay modal) -----
  const renderChooser = () => (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.modalTitleWrap}>
          <h3 className={styles.modalTitle}>Choose data to edit</h3>
          <p className={styles.modalSubtitle}>Select how you'd like to load your inventory items.</p>
        </div>
        <button className={styles.modalClose} onClick={() => setFilterModalOpen(false)} aria-label="Close">
          &times;
        </button>
      </div>

      <div className={styles.modalBody}>
        <div className={styles.typeGrid}>
          {FILTER_TYPES.map(({ val, lbl, desc }) => (
            <button
              key={val}
              className={cx(styles.typeCard, selectedFilter === val && styles.typeCardActive)}
              onClick={() => selectFilterType(val)}
            >
              <span className={styles.typeIcon}><TypeIcon type={val} /></span>
              <span className={styles.typeLabel}>{lbl}</span>
              <span className={styles.typeDesc}>{desc}</span>
            </button>
          ))}
        </div>

        <div className={styles.selects}>
          {(selectedFilter === "filter1" || selectedFilter === "filter3") && (
            <div className={styles.selectGroup}>
              <label>School</label>
              <select value={selectedSchool} onChange={handleSchoolChange}>
                <option value="">Select a school</option>
                {schoolList.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {((selectedFilter === "filter1" && selectedSchool) || selectedFilter === "filter2") && (
            <div className={styles.selectGroup}>
              <label>Item Type</label>
              <select value={selectedItemType} onChange={handleItemTypeChange}>
                <option value="">Select an item type</option>
                {(selectedFilter === "filter1" ? filteredItemTypeList : itemTypeList).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className={styles.modalFooter}>
        {hasData && (
          <button className={styles.toolbarBtn} onClick={() => setFilterModalOpen(false)}>Cancel</button>
        )}
        <button className={styles.doneBtn} onClick={() => setFilterModalOpen(false)} disabled={!hasData}>
          {hasData ? "Done" : "Pick a filter…"}
        </button>
      </div>
    </>
  );

  // ================= JSX =================
  return (
    <div className={cx(loading && styles.blur)}>
      {loading && <div className={styles.spinner} />}

      <div className={styles.page}>
        <div className={styles.topBar}>
          <span className={styles.topBarSpacer} />
          <button className={styles.changeFilterBtn} onClick={openChooser}>Filters</button>
        </div>

        {hasData ? (
          <>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <SearchIcon />
                <input className={styles.searchInput} type="text" placeholder="Search all columns…"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <input className={styles.qtyFilter} type="text" inputMode="numeric" placeholder="Max Qty"
                value={maxQuantity}
                onChange={(e) => /^[0-9]*$/.test(e.target.value) && setMaxQuantity(e.target.value)} />

              <div className={styles.groupControl}>
                <span className={styles.groupLabel}>Group</span>
                <div className={styles.segment}>
                  <button className={cx(styles.seg, !groupBy && styles.segActive)} onClick={() => chooseGroup("")}>None</button>
                  {GROUPABLE.map((k) => (
                    <button key={k} className={cx(styles.seg, groupBy === k && styles.segActive)}
                      onClick={() => chooseGroup(groupBy === k ? "" : k)}>
                      {COLUMNS.find((c) => c.key === k)?.label}
                    </button>
                  ))}
                </div>
              </div>

              <span className={styles.toolbarSpacer} />
              <button className={styles.toolbarBtn} onClick={clearAll}>Clear filters &amp; sort</button>
              {hasEdits && (
                <button className={styles.resetBtn} onClick={resetEdits}>Reset changes ({diffs.length})</button>
              )}
            </div>

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <colgroup>
                  {COLUMNS.map((c) => <col key={c.key} style={{ width: colWidths[c.key] }} />)}
                  <col style={{ width: colWidths[ACTION_KEY] }} />
                </colgroup>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className={styles.th}>
                        <div className={styles.thInner}>
                          <span className={styles.thLabel} onClick={() => handleSort(c.key)} title={c.label}>
                            {c.label}
                            <span className={styles.sortIndicator}>
                              {sortConfig.key === c.key
                                ? sortConfig.direction === "asc" ? " \u25B2" : sortConfig.direction === "desc" ? " \u25BC" : ""
                                : ""}
                            </span>
                          </span>
                          <button className={cx(styles.filterIcon, columnFilters[c.key] && styles.filterIconActive)}
                            onClick={(e) => openFilter(e, c.key)} title="Filter">&#9662;</button>
                        </div>
                        <span className={styles.resizer} onMouseDown={(e) => startResize(e, c.key)} />
                      </th>
                    ))}
                    <th className={styles.th}>
                      <div className={styles.thInner}><span className={styles.thLabel}>Action</span></div>
                      <span className={styles.resizer} onMouseDown={(e) => startResize(e, ACTION_KEY)} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((gp) => (
                    <React.Fragment key={gp.key}>
                      {groupBy && (
                        <tr className={styles.groupHeader} onClick={() => toggleGroup(gp.key)}>
                          <td colSpan={COLUMNS.length + 1}>
                            <span className={styles.groupCaret}>{collapsedGroups[gp.key] ? "\u25B6" : "\u25BC"}</span>
                            {COLUMNS.find((c) => c.key === groupBy)?.label}: <strong>{gp.key}</strong>
                            <span className={styles.groupCount}>{gp.items.length}</span>
                          </td>
                        </tr>
                      )}
                      {(!groupBy || !collapsedGroups[gp.key]) &&
                        gp.items.map((item) => {
                          const id = getRowId(item);
                          const rowIdx = indexById[id];
                          const fillField = (fill && rowIdx >= fillLo && rowIdx <= fillHi) ? fill.field : null;
                          return (
                            <Row
                              key={item.itemBarcodeID ?? id}
                              item={item}
                              rowEdit={editableData[id]}
                              rowIdx={rowIdx}
                              fillField={fillField}
                              onFieldChange={handleFieldChange}
                              onNumberChange={onNumberChange}
                              onCellKeyDown={handleCellKeyDown}
                              onPaste={handlePaste}
                              onStartFill={startFill}
                              registerRef={registerRef}
                              onRowMouseEnter={onRowMouseEnter}
                            />
                          );
                        })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {(hasEdits || isUpdating) && (
              <div className={styles.controls}>
                <button className={styles.submitBtn} onClick={handleSubmitClick} disabled={isUpdating}>
                  {isUpdating ? "Updating items…" : `Review & Submit (${diffs.length})`}
                </button>
              </div>
            )}
            {statusMessage && <pre className={styles.status}>{statusMessage}</pre>}
          </>
        ) : (
          // ---------- NO DATA: inline (non-overlay) chooser on the page ----------
          <div className={styles.fillArea}>
            {filterModalOpen && !chooserOverlay ? (
              <div className={styles.inlineCard}>{renderChooser()}</div>
            ) : (
              <div className={styles.emptyState}>
                <svg className={styles.emptyIcon} viewBox="0 0 24 24" width="56" height="56" fill="none"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="9" x2="9" y2="20" />
                </svg>
                <p className={styles.emptyTitle}>No data loaded yet</p>
                <p className={styles.emptyText}>Choose a filter to load inventory items for editing.</p>
                <button className={styles.changeFilterBtn} onClick={openChooser}>
                  Choose data to edit
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------- column filter popover (global) ---------- */}
      {activeFilter && (
        <div ref={popoverRef} className={styles.filterPopover} style={{ top: activeFilter.y, left: activeFilter.x }}>
          <button className={styles.popSortBtn} onClick={() => setSortConfig({ key: activeFilter.key, direction: "asc" })}>↑ Sort A → Z</button>
          <button className={styles.popSortBtn} onClick={() => setSortConfig({ key: activeFilter.key, direction: "desc" })}>↓ Sort Z → A</button>
          <div className={styles.popDivider} />
          <input className={styles.popSearch} placeholder="Search values" value={popSearch} onChange={(e) => setPopSearch(e.target.value)} />
          <label className={styles.popItem}>
            <input type="checkbox" checked={popAllChecked} onChange={() => toggleSelectAll(activeFilter.key)} />
            <span><strong>(Select All)</strong></span>
          </label>
          <div className={styles.popList}>
            {popValues.map((v) => (
              <label key={v} className={styles.popItem}>
                <input type="checkbox" checked={isValChecked(activeFilter.key, v)} onChange={() => toggleValue(activeFilter.key, v)} />
                <span>{v}</span>
              </label>
            ))}
          </div>
          <div className={styles.popFooter}>
            <button className={styles.popClear} onClick={() => clearColFilter(activeFilter.key)}>Clear</button>
            <button className={styles.popOk} onClick={closeFilter}>OK</button>
          </div>
        </div>
      )}

      {/* ---------- filter-selection OVERLAY modal (sticky mode, decided on open) ---------- */}
      {filterModalOpen && chooserOverlay && (
        <div className={styles.overlay} onMouseDown={() => setFilterModalOpen(false)}>
          <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            {renderChooser()}
          </div>
        </div>
      )}

      {/* ---------- review (diff) modal ---------- */}
      {showDiff && (
        <div className={styles.overlay} onMouseDown={() => setShowDiff(false)}>
          <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Review changes — {diffs.length} item{diffs.length !== 1 ? "s" : ""}, {diffs.reduce((n, d) => n + d.changes.length, 0)} field{diffs.reduce((n, d) => n + d.changes.length, 0) !== 1 ? "s" : ""}</h3>
              <button className={styles.modalClose} onClick={() => setShowDiff(false)}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              <table className={styles.diffTable}>
                <thead><tr><th>Item</th><th>Field</th><th>Old</th><th></th><th>New</th></tr></thead>
                <tbody>
                  {diffs.map((d) =>
                    d.changes.map((ch, i) => (
                      <tr key={d.id + ch.key}>
                        <td>{i === 0 ? (d.item.itemCode || d.item.description || d.item.sno) : ""}</td>
                        <td>{ch.label}</td>
                        <td className={styles.diffOld}>{String(ch.old ?? "")}</td>
                        <td className={styles.diffArrow}>→</td>
                        <td className={styles.diffNew}>{String(ch.next ?? "")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.toolbarBtn} onClick={() => setShowDiff(false)}>Cancel</button>
              <button className={styles.submitBtn} onClick={confirmSubmit}>Confirm &amp; Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterEditStock;